export const MAX_GUESTS = 5;
export const FADER_STRIP_WIDTH_PX = 120;
export const FADER_STRIP_GAP_PX = 10;
export const MIXER_PANEL_CHROME_PX = 56;

export const canManageRoom = (room) => room?.can_manage === true;

/** Pixel width for the Server mix panel based on visible channel count. */
export const mixerWidthForChannels = (channelCount) => {
  const count = Math.max(2, Math.min(MAX_GUESTS + 2, Number(channelCount) || 2));
  return MIXER_PANEL_CHROME_PX
    + (count * FADER_STRIP_WIDTH_PX)
    + ((count - 1) * FADER_STRIP_GAP_PX);
};

export const isCueModeActive = (channels = []) =>
  (channels || []).some((channel) => Boolean(channel?.pfl));

export const shouldMonitorLocalHostAudio = (channels = []) =>
  (channels || []).some((channel) => channel?.role === 'host' && Boolean(channel?.pfl));

const participantKeys = (participant) =>
  [
    participant?.identity,
    participant?.livekit_identity,
    participant?.sid,
    participant?.id,
    participant?.participantId,
    participant?.participant_id,
  ]
    .filter((value) => value != null && value !== '')
    .map(String);

const channelKeys = (channel) =>
  [
    channel?.identity,
    channel?.participantId,
    channel?.id,
  ]
    .filter((value) => value != null && value !== '')
    .map(String);

export const isProgramReturnParticipant = (participant) => {
  const identity = String(participant?.identity || participant?.livekit_identity || '');
  if (identity.startsWith('bridge-')) return true;
  return getParticipantRole(participant) === 'program';
};

/**
 * Host-monitor attach rule: in cue mode, only PFL'd strips are audible.
 * Program cue listens to the LiveKit program-return (bridge) participant.
 * Cue never changes what is mixed to air.
 */
export const shouldAttachParticipantAudio = (participant, channels = []) => {
  if (!participant) return false;
  if (!isCueModeActive(channels)) return true;

  const cued = (channels || []).filter((channel) => channel.pfl);
  if (cued.some((channel) => channel.role === 'program') && isProgramReturnParticipant(participant)) {
    return true;
  }

  const keys = participantKeys(participant);
  return cued.some((channel) => {
    if (channel.role === 'program') return false;
    return channelKeys(channel).some((key) => keys.includes(key));
  });
};

export const clampGain = (value, fallback = 0.75) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(1, Math.max(0, number)) : fallback;
};

export const gainToDecibels = (gain) => {
  const normalized = clampGain(gain, 0);
  return normalized === 0 ? -Infinity : 20 * Math.log10(normalized);
};

export const decibelsToGain = (decibels) => {
  const db = Number(decibels);
  if (!Number.isFinite(db) || db <= -90) return 0;
  return clampGain(10 ** (db / 20), 0);
};

export const formatGain = (gain) => {
  const decibels = gainToDecibels(gain);
  return Number.isFinite(decibels) ? `${decibels.toFixed(1)} dB` : '-∞ dB';
};

const metadataFor = (participant) => {
  if (!participant) return {};
  if (participant.metadata && typeof participant.metadata === 'object') return participant.metadata;
  if (typeof participant.metadata !== 'string') return {};
  try {
    return JSON.parse(participant.metadata);
  } catch {
    return {};
  }
};

export const getParticipantRole = (participant) => {
  const metadata = metadataFor(participant);
  const role = String(
    participant?.role ||
    participant?.type ||
    participant?.channel_type ||
    participant?.participant_type ||
    metadata.role ||
    metadata.participantRole ||
    metadata.participantType ||
    participant?.identity ||
    participant?.livekit_identity ||
    ''
  ).toLowerCase();
  if (role.includes('program') || role.includes('bridge')) return 'program';
  if (role.includes('master')) return 'master';
  if (role.includes('host') || role.includes('presenter') || role.includes('producer')) return 'host';
  return 'guest';
};

const participantId = (participant, index) =>
  participant?.channelId ||
  participant?.channel_key ||
  participant?.id ||
  participant?.sid ||
  participant?.identity ||
  `guest-${index + 1}`;

const participantName = (participant, role, index) =>
  participant?.displayName ||
  participant?.display_name ||
  participant?.label ||
  participant?.name ||
  participant?.identity ||
  (role === 'program' ? 'Program' : role === 'host' ? 'Host' : `Guest ${index + 1}`);

export const normalizeMixerChannel = (channel, defaults, index = 0) => {
  const role = getParticipantRole(channel);
  const fallbackGain = defaults?.[role] ?? defaults?.guest ?? 0.75;
  const meterData = channel?.meter && typeof channel.meter === 'object' ? channel.meter : null;
  const gain = channel?.gain !== undefined
    ? channel.gain
    : channel?.gainDb !== undefined
      ? decibelsToGain(channel.gainDb)
      : channel?.gain_db !== undefined
        ? decibelsToGain(channel.gain_db)
        : fallbackGain;
  const peakDb = meterData?.peakDb ?? meterData?.peak_db;
  return {
    id: String(participantId(channel, index)),
    participantId:
      channel?.participantId ||
      channel?.participant_id ||
      channel?.participantSid ||
      channel?.identity ||
      channel?.livekit_identity ||
      null,
    identity: channel?.identity || channel?.livekit_identity || null,
    role,
    name: participantName(channel, role, index),
    gain: clampGain(gain, fallbackGain),
    muted: Boolean(channel?.muted),
    onAir: (channel?.onAir ?? channel?.on_air) !== false,
    pfl: Boolean(channel?.pfl),
    meter: Math.min(1, Math.max(0, Number(
      Number.isFinite(Number(peakDb))
        ? decibelsToGain(peakDb)
        : channel?.meter || channel?.audioLevel || channel?.audio_level || 0
    ))),
  };
};

const preferredChannel = (current, candidate) => {
  if (!current) return candidate;
  const currentLooksSynthetic = current.id === current.role;
  return currentLooksSynthetic ? candidate : current;
};

export const buildMixerChannels = ({
  channels = [],
  participants = [],
  defaultFaders = { program: 0.85, host: 0.8, guest: 0.75 },
} = {}) => {
  const source = [
    ...channels,
    ...participants.filter((participant) => {
      const role = getParticipantRole(participant);
      if (role === 'program' && channels.some((channel) => getParticipantRole(channel) === 'program')) {
        return false;
      }
      const id = String(participantId(participant, 0));
      return !channels.some((channel) => {
        const channelParticipantId =
          channel?.livekit_identity ||
          channel?.identity ||
          channel?.participantId ||
          channel?.participant_id ||
          channel?.participantSid ||
          channel?.id;
        return channelParticipantId != null && String(channelParticipantId) === id;
      });
    }),
  ];

  const normalized = source.map((item, index) =>
    normalizeMixerChannel(item, defaultFaders, index)
  );

  let program = normalized.filter((channel) => channel.role === 'program')
    .reduce(preferredChannel, null);
  let host = normalized.filter((channel) => channel.role === 'host')
    .reduce(preferredChannel, null);
  const guests = normalized
    .filter((channel) => channel.role === 'guest')
    .filter((channel, index, all) => {
      const key = channel.identity || channel.participantId || channel.id;
      return all.findIndex((item) =>
        (item.identity || item.participantId || item.id) === key
      ) === index;
    })
    .slice(0, MAX_GUESTS);

  if (!program) {
    program = normalizeMixerChannel(
      { id: 'program', role: 'program', name: 'Program', onAir: true },
      defaultFaders
    );
  }
  if (!host) {
    host = normalizeMixerChannel(
      { id: 'host', role: 'host', name: 'Host', onAir: true },
      defaultFaders
    );
  }

  return [program, host, ...guests];
};

export const mergeMixerChannels = (incoming, current) => {
  const currentById = new Map((current || []).map((channel) => [String(channel.id), channel]));
  return (incoming || []).map((channel) => {
    const previous = currentById.get(String(channel.id));
    if (!previous) return channel;
    return {
      ...previous,
      ...channel,
      meter: channel.meter ?? previous.meter,
    };
  });
};

export const isTerminalLiveRoom = (room) =>
  room?.active === false
  || room?.status === 'ended'
  || room?.status === 'failed'
  || room?.status === 'ending';

export const patchMixerChannel = (channels, channelId, changes = {}) =>
  channels.map((channel) => {
    if (String(channel.id) !== String(channelId)) return channel;
    // Persist the mixer key (program / host / participant:…) even when the
    // API returns a Postgres UUID as `id`. Meters and React strip keys use it.
    const nextId = changes.channel_key ? String(changes.channel_key) : channel.id;
    return {
      ...channel,
      id: nextId,
      participantId: changes.participantId ?? changes.participant_id ?? channel.participantId,
      identity: changes.identity ?? changes.livekit_identity ?? channel.identity,
      name: changes.name ?? changes.label ?? channel.name,
      gain: changes.gain !== undefined
        ? clampGain(changes.gain, channel.gain)
        : changes.gainDb !== undefined
          ? decibelsToGain(changes.gainDb)
          : changes.gain_db !== undefined
            ? decibelsToGain(changes.gain_db)
            : channel.gain,
      muted: changes.muted !== undefined ? Boolean(changes.muted) : channel.muted,
      onAir: changes.onAir ?? changes.on_air ?? channel.onAir,
      pfl: changes.pfl !== undefined ? Boolean(changes.pfl) : channel.pfl,
      meter: typeof changes.meter === 'number'
        ? Math.min(1, Math.max(0, Number(changes.meter) || 0))
        : channel.meter,
    };
  });
